import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import Department01 from '../../../assets/image/department/department-1.jpg';
import Department02 from '../../../assets/image/department/department-2.jpg';

function HousingAndUrbanDevelopment() {

  const [Faq, setFaq] = useState(0);

  // pick up theme colors from existing DOM
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [bodyColor, setBodyColor] = useState('');

  useEffect(() => {
    if (titleRef.current) setTitleColor(window.getComputedStyle(titleRef.current).color);
    if (bodyRef.current) setBodyColor(window.getComputedStyle(bodyRef.current).color);
  }, []);

  // compact stacked subtitle/description layout to avoid awkward wraps
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, color: titleColor || '#0f172a' };
  const valText = { fontSize: '0.95rem', lineHeight: 1.6, color: bodyColor || undefined };

  const Drop = [
    {
      Que: "How can I apply for housing assistance?",
      Answer:
        "Apply online through the Housing & Urban Development portal or at a local service center. You’ll typically need proof of identity, residency, income, household size, and current housing status. If you qualify, we’ll help you enroll in vouchers, public/affordable units, or short-term rental assistance."
    },
    {
      Que: "What resources are available for tenants?",
      Answer:
        "Rental assistance, fair-housing complaint support, landlord–tenant mediation, legal referrals for eviction prevention, and help understanding leases, notices, and rent increases. Many resources are available in multiple languages."
    },
    {
      Que: "How does HUD support community development?",
      Answer:
        "Through neighborhood revitalization, small-business corridors, accessibility upgrades, infrastructure improvements, and preservation of existing affordable units. We coordinate grants and public–private partnerships to boost housing supply and local amenities."
    },
    {
      Que: "What documents do developers need for incentives?",
      Answer:
        "Concept plans, site control, zoning confirmation, environmental review records, affordability commitments, and basic pro formas. Pre-application meetings are recommended to align on timelines and requirements."
    },
    {
      Que: "How are anti-displacement protections enforced?",
      Answer:
        "Projects using public funds follow relocation rules, right-to-return policies where applicable, income-based rent limits, and ongoing compliance monitoring. Tenants receive notices, counseling, and access to legal resources."
    }
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">

            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={Department01} className="img-fluid" alt="Housing and Community Development" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Housing and Community Development</h4>
                <p style={{ color: titleColor || '#0f172a' }}>
                  We expand access to safe, affordable housing and strengthen neighborhoods through planning, investment,
                  and fair-housing enforcement. Our priorities: housing stability, preservation, and equitable growth.
                </p>
                <p>
                  Core work includes rental assistance and voucher navigation, affordable housing production and preservation,
                  fair-housing protections, landlord–tenant support, and neighborhood revitalization. We collaborate with
                  municipalities, nonprofit developers, housing authorities, lenders, and community groups to align funding and
                  speed delivery of homes people can afford.
                </p>
              </div>

              <div className="department-details-box">
                <div className="department-details-policy">
                  <span>
                    Promoting affordable housing and sustainable development.
                  </span>
                </div>
                <p ref={bodyRef}>
                  Our initiatives span down-payment support, rehab and weatherization, accessibility retrofits, and main-street
                  improvements. We use data-driven targeting to reduce cost burden, prevent displacement, and improve access to jobs,
                  transit, and essential services.
                </p>
              </div>

              <div className="department-details-benefits-inner-box">
                <div className="row row-gutter-30">
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box-image">
                      <img src={Department02} className="img-fluid" alt="Affordable Housing" />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="department-details-benefits-box">
                      <h3>Our Benefits</h3>
                      <p>
                        Stable, affordable homes and revitalized neighborhoods that support opportunity.
                      </p>
                      <ul className="list-unstyled list-style" style={{ marginTop: '-15px' }}>
                        <li>
                          <Link to="/department/housing/affordable-housing-programs">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Affordable Housing Programs
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/housing/community-development">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Community Development and Revitalization
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/housing/tenant-rights">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Tenant Rights and Rental Assistance
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/housing/zoning-permits">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Zoning and Permits Services
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Outcomes section */}
              <div className="department-details-content-box" style={{ marginTop: '10px' }}>
                <h4
                  className="department-details-title"
                  style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}
                >
                  Outcomes we aim for
                </h4>
                <p style={{ color: bodyColor || undefined, fontSize: '0.95rem' }}>
                  More rent-stable households, faster time-to-permit for compliant projects, preserved and newly created
                  affordable units, fewer evictions through early intervention, stronger anti-displacement protections,
                  and walkable districts with access to transit, jobs, and services.
                </p>
              </div>

              {/* What We Do — compact stacked layout */}
              <div className="department-details-benefits-box">
                <h3>What We Do</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Access to Housing & Voucher Navigation</div>
                      <div style={valText}>
                        Eligibility screening, waitlist support, landlord outreach, portability guidance, and placement into
                        affordable or supportive units.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Production, Preservation & Rehab</div>
                      <div style={valText}>
                        Gap financing, preservation of at-risk properties, code and safety upgrades, energy efficiency, and
                        accessibility retrofits to extend the life of existing homes.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Community Development & Small-Area Plans</div>
                      <div style={valText}>
                        Streetscapes, lighting, ADA improvements, mixed-use corridors, and catalytic investments that attract
                        private capital while protecting current residents.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Zoning, Permits & Code Compliance</div>
                      <div style={valText}>
                        Pre-application guidance, predictable timelines, clear checklists, by-right pathways where applicable,
                        and consistent inspections to keep projects safe and on schedule.
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Who We Serve */}
              <div className="department-details-benefits-box">
                <h3>Who We Serve</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Rent-Burdened Households</div>
                      <div style={valText}>Families and individuals paying an outsized share of income toward rent who need stability.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Seniors & People with Disabilities</div>
                      <div style={valText}>Accessible units, home modifications, utility help, and supportive services.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>First-Time Homebuyers</div>
                      <div style={valText}>Counseling, down-payment assistance, and connections to safe, affordable mortgages.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Small Landlords</div>
                      <div style={valText}>Rehab/energy programs, fair-housing guidance, and compliance support to keep units affordable.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Funding & Partnerships */}
              <div className="department-details-benefits-box">
                <h3>Funding & Partnerships</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Public & Philanthropic Capital</div>
                      <div style={valText}>Blend local, state, federal, and philanthropic funds to close financing gaps and scale impact.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Developer Pipelines</div>
                      <div style={valText}>Technical assistance for nonprofit and emerging developers to deliver quality, affordable projects.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Transit & Infrastructure Alignment</div>
                      <div style={valText}>Coordinate housing with transit corridors, schools, utilities, and broadband to reduce household costs.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Fair Housing & Tenant Protections */}
              <div className="department-details-benefits-box">
                <h3>Fair Housing & Tenant Protections</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Anti-Discrimination Enforcement</div>
                      <div style={valText}>Intake and investigation of complaints, outreach to vulnerable groups, and corrective actions when required.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Eviction Prevention</div>
                      <div style={valText}>Early warning, rent assistance, mediation, legal aid referrals, and rapid rehousing coordination.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* How to Apply */}
              <div className="department-details-content-box">
                <h4 className="department-details-title" style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}>
                  How to Apply
                </h4>
                <ol style={{ paddingLeft: '18px', color: bodyColor || undefined, fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <li>Start an application online or visit a service center; interpreter services available.</li>
                  <li>Gather documents: ID, proof of residency, income, household size, and current housing status.</li>
                  <li>Complete eligibility screening and get matched to vouchers, affordable units, or homeowner resources.</li>
                  <li>Receive next steps, timelines, and ongoing case management if needed.</li>
                </ol>
              </div>

              <div className="accordian-box">
                {Drop.map((items, index) => (
                  <div className={`accordian-box-item ${Faq !== index && "active"}`} key={index}>
                    <div className="accordian-title">
                      <h5>{items.Que}</h5>
                      <i className="fa-solid fa-angle-right" onClick={() => setFaq(index)} />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: "block" }}>
                        <p>{items.Answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default HousingAndUrbanDevelopment;
